import { getAbsoluteLocaleUrl, resolvePublicSeoBaseUrl } from '@/i18n/urls'
import type { Locale } from '@/i18n/routing'

export type BreadcrumbListItem = { title: string; link?: string }

type BuildBreadcrumbListJsonLdParams = {
  items: BreadcrumbListItem[]
  baseUrl: string
  locale: Locale | string
  /** Absolute-path href for the current page (used when last crumb has no link). */
  currentHref: string
}

function toAbsoluteUrl(
  baseUrl: string,
  locale: Locale | string,
  href: string,
): string {
  const [pathWithQuery, hash = ''] = href.split('#')
  const path = pathWithQuery?.split('?')[0] || '/'
  const absolute = getAbsoluteLocaleUrl(
    resolvePublicSeoBaseUrl(baseUrl),
    locale,
    path || '/',
  )
  return hash ? `${absolute}#${hash}` : absolute
}

/**
 * TZ 10.1 nested BreadcrumbList:
 * itemListElement[].item = { "@id": url, "name": label }
 */
export function buildBreadcrumbListJsonLd({
  items,
  baseUrl,
  locale,
  currentHref,
}: BuildBreadcrumbListJsonLdParams) {
  if (!items.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((crumb, index) => {
      const isLast = index === items.length - 1
      const href = isLast ? currentHref : crumb.link || currentHref

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': toAbsoluteUrl(baseUrl, locale, href),
          name: crumb.title,
        },
      }
    }),
  }
}
